import en from './en';
import type { AdminUiCopy } from './types';

type ExtendedSections = Pick<
  AdminUiCopy,
  | 'ownerStaff'
  | 'ownerBlogManager'
  | 'ownerBlogEditor'
  | 'superAdminDashboard'
  | 'superAdminSecurity'
  | 'superAdminUsers'
  | 'superAdminDemoRequests'
  | 'superAdminUserDetail'
>;

const deExtendedSections: ExtendedSections = {
  ownerStaff: {
    ownerTools: 'Inhaber-Tools',
    notAuthorized: 'Nicht autorisiert',
    restrictedBody:
      'Dieser Bereich ist nur für Plattform-Inhaber. Inhaber-Tools steuern Staff-Zugriffe und schreiben direkt in Produktionsberechtigungen.',
    ownerAccessRequired: 'Inhaberzugriff erforderlich',
    notInOwnerEmails: 'Ihre E-Mail {email} ist nicht in PLATFORM_OWNER_EMAILS eingetragen.',
    addAndReauth:
      'Fügen Sie die Adresse dort hinzu und melden Sie sich erneut über Google/GitHub SSO an. In Produktion wird der Inhaberstatus aus Umgebungswerten und SSO-Sitzung bestimmt.',
    manageStaffBody:
      'Verwalten Sie Staff-Zugriffe (Super-Admin). Änderungen greifen sofort und werden für Audits protokolliert. Verwenden Sie Super-Admin-Tools zum Sperren oder Reaktivieren von Kundenkonten.',
    grantRevokeTitle: 'Staff-Zugriff vergeben/entziehen',
    email: 'E-Mail',
    action: 'Aktion',
    reason: 'Grund',
    grantStaff: 'Staff vergeben',
    revokeStaff: 'Staff entziehen',
    working: 'Wird ausgeführt...',
    enterEmail: 'Geben Sie eine E-Mail ein, um fortzufahren',
    provideReason: 'Bitte geben Sie einen Grund an (für Audit-Logs)',
    currentStaff: 'Aktuelle Staff-Konten',
    noStaff: 'Keine Staff-Konten.',
    tableEmail: 'E-Mail',
    tableId: 'ID',
    tableActions: 'Aktionen',
    revoke: 'Entziehen',
    prev: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {page} von {total}',
    recentActions: 'Letzte Inhaber-Aktionen',
    noRecentActions: 'Keine aktuellen Aktionen.',
    grantDialogTitle: 'Staff-Zugriff vergeben?',
    revokeDialogTitle: 'Staff-Zugriff entziehen?',
    grantDialogDescription: 'Damit wird {email} vollständiger Plattformzugriff gewährt.',
    revokeDialogDescription: 'Damit wird {email} plattformweiter Zugriff entzogen.',
    confirmGrant: 'Vergeben',
    confirmRevoke: 'Entziehen',
    grantSuccess: 'Staff für {email} vergeben',
    revokeSuccess: 'Staff für {email} entzogen',
    failedUpdateStaff: 'Staff konnte nicht aktualisiert werden',
    failedRevoke: 'Entzug fehlgeschlagen',
    revokeReasonPlaceholder: 'Grund für den Entzug',
    revokeReasonHint: 'Wird für Audit-Logs verwendet; bitte kurz halten.',
    ownerManagedUpdateDefault: 'Vom Inhaber verwaltete Staff-Aktualisierung',
    accessNoLongerRequiredDefault: 'Zugriff nicht mehr erforderlich',
  },
  ownerBlogManager: {
    title: 'Blog-Manager',
    subtitle: 'Blogbeiträge erstellen und verwalten',
    accessRestricted: 'Zugriff eingeschränkt',
    accessBody: 'Die Blogverwaltung erfordert Inhaber- oder Super-Admin-Zugriff.',
    newPost: 'Neuer Beitrag',
    all: 'Alle',
    draft: 'Entwurf',
    published: 'Veröffentlicht',
    loadingPosts: 'Beiträge werden geladen...',
    noPosts: 'Noch keine Beiträge. Erstellen Sie Ihren ersten Blogbeitrag.',
    createFirstPost: 'Erstellen Sie Ihren ersten Beitrag, um Updates zu veröffentlichen.',
    createPost: 'Beitrag erstellen',
    tableTitle: 'Titel',
    tableStatus: 'Status',
    tableAuthor: 'Autor',
    tableDate: 'Datum',
    tableActions: 'Aktionen',
    edit: 'Bearbeiten',
    view: 'Ansehen',
    delete: 'Löschen',
    previous: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {page} von {total}',
    deleteTitle: 'Beitrag löschen?',
    deleteBody: 'Diese Aktion kann nicht rückgängig gemacht werden. Der Beitrag wird dauerhaft gelöscht.',
    cancel: 'Abbrechen',
    confirmDelete: 'Löschen',
    toastPostDeleted: 'Beitrag gelöscht',
    toastDeleteFailed: 'Beitrag konnte nicht gelöscht werden',
  },
  ownerBlogEditor: {
    title: 'Blog-Editor',
    accessRestricted: 'Zugriff eingeschränkt',
    accessBody: 'Die Blogverwaltung erfordert Inhaber- oder Super-Admin-Zugriff.',
    editPost: 'Beitrag bearbeiten',
    newPost: 'Neuer Beitrag',
    backToManager: 'Zurück zum Blog-Manager',
    saveDraft: 'Entwurf speichern',
    publish: 'Veröffentlichen',
    saving: 'Wird gespeichert...',
    publishing: 'Wird veröffentlicht...',
    postTitlePlaceholder: 'Beitragstitel',
    status: 'Status',
    draft: 'Entwurf',
    published: 'Veröffentlicht',
    slug: 'URL-Slug',
    slugPlaceholder: 'beitrags-url-slug',
    excerpt: 'Kurzfassung',
    excerptPlaceholder: 'Kurze Zusammenfassung für die Blogübersicht...',
    featuredImageUrl: 'URL des Titelbilds',
    tags: 'Tags',
    tagsPlaceholder: 'EUDI, Produktupdate, Engineering',
    commaSeparated: 'Kommagetrennt',
    authorName: 'Autorname',
    authorNamePlaceholder: 'Name des Autors',
    authorAvatarUrl: 'URL des Autor-Avatars',
    remove: 'Entfernen',
    importMarkdown: 'Markdown importieren',
    markdown: 'Markdown',
    markdownPlaceholder: 'Markdown hier einfügen...',
    preview: 'Vorschau',
    previewPlaceholder: 'Die Vorschau erscheint hier...',
    cancel: 'Abbrechen',
    import: 'Importieren',
    titleRequired: 'Titel ist erforderlich',
    contentRequired: 'Inhalt ist erforderlich',
    failedLoadPost: 'Beitrag konnte nicht geladen werden',
    recoveredDraft: 'Ungespeicherten Entwurf wiederhergestellt',
    postPublished: 'Beitrag veröffentlicht',
    draftSaved: 'Entwurf gespeichert',
    draftCreated: 'Entwurf erstellt',
    failedSavePost: 'Beitrag konnte nicht gespeichert werden',
    imageUploaded: 'Bild hochgeladen',
    avatarUploaded: 'Avatar hochgeladen',
    uploadFailed: 'Upload fehlgeschlagen',
    markdownImported: 'Markdown importiert',
    startWritingPlaceholder: 'Beginnen Sie mit dem Schreiben Ihres Blogbeitrags...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Plattformmetriken werden geladen...',
    platformOverview: 'Plattformübersicht',
    globalMetrics: 'Globale Metriken und Performance-Analysen',
    noMetrics: 'Keine Metriken verfügbar',
    quickActions: 'Schnellaktionen',
    platformSecurity: 'Plattformsicherheit',
    viewAllUsers: 'Alle Nutzer anzeigen',
    demoRequests: 'Demo-Anfragen',
    trialUsers: 'Trial-Nutzer',
    suspendedUsers: 'Gesperrte Nutzer',
    userMetrics: 'Nutzermetriken',
    totalUsers: 'Nutzer gesamt',
    activeUsers: 'Aktive Nutzer',
    activePaidUsers: 'Aktive zahlende Nutzer',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Monatlich wiederkehrender Umsatz',
    ofTotal: '{value}% von gesamt',
    ofActiveUsers: '{value}% der aktiven Nutzer',
    planDistribution: 'Tarifverteilung',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Aktive Nutzer{totalSuffix}',
    verificationSessions: 'Verifizierungssitzungen',
    totalSessions: 'Sitzungen gesamt',
    thisMonth: 'Dieser Monat',
    lastMonth: 'Letzter Monat',
    growth: 'Wachstum',
    liveVerifications: 'Live-Verifizierungen',
    allTime: 'Gesamt',
    apiKeys: 'API-Schlüssel',
    totalKeys: 'Schlüssel gesamt',
    activeKeys: 'Aktive Schlüssel',
    liveKeys: 'Live-Schlüssel',
    productionEnvironment: 'Produktivumgebung',
  },
  superAdminSecurity: {
    superAdmin: 'Super-Admin',
    platformSecurity: 'Plattformsicherheit',
    subtitle: 'Mandantenübergreifende Anomalieübersicht für Staff-Nutzer.',
    totalAlerts: 'Warnungen gesamt',
    activeAlerts: 'Aktive Warnungen',
    critical: 'Kritisch',
    topMerchantsLast7d: 'Top-Merchants (letzte 7 T.)',
    filters: 'Filter',
    severity: 'Schweregrad',
    status: 'Status',
    merchantIdOptional: 'Merchant-ID (optional)',
    show: 'Anzeigen',
    any: 'Beliebig',
    low: 'Niedrig',
    medium: 'Mittel',
    high: 'Hoch',
    active: 'Aktiv',
    investigating: 'In Prüfung',
    resolved: 'Gelöst',
    falsePositive: 'Fehlalarm',
    refresh: 'Aktualisieren',
    securityAlerts: 'Sicherheitswarnungen',
    noAlerts: 'Keine Warnungen für die aktuellen Filter.',
    time: 'Zeit',
    merchant: 'Merchant',
    type: 'Typ',
    pageOf: 'Seite {page} von {total}',
    prev: 'Zurück',
    next: 'Weiter',
  },
  superAdminUsers: {
    superAdmin: 'Super-Admin',
    allUsers: 'Alle Nutzer',
    subtitle: 'Plattformweite Nutzerverwaltung und Analysen',
    totalUsers: 'Nutzer gesamt',
    activeUsers: 'Aktive Nutzer',
    trialUsers: 'Trial-Nutzer',
    paidUsers: 'Zahlende Nutzer',
    filters: 'Filter',
    exporting: 'Export läuft...',
    exportCsv: 'CSV exportieren',
    hideFilters: 'Filter ausblenden',
    showFilters: 'Filter anzeigen',
    search: 'Suche',
    searchPlaceholder: 'E-Mail, Name oder Firma...',
    planTier: 'Tarifstufe',
    allTiers: 'Alle Stufen',
    trial: 'Trial',
    pro: 'Pro',
    business: 'Business',
    status: 'Status',
    allStatuses: 'Alle Status',
    active: 'Aktiv',
    suspended: 'Gesperrt',
    loadingUsers: 'Nutzer werden geladen...',
    noUsersFound: 'Keine Nutzer gefunden',
    tableUser: 'Nutzer',
    tablePlan: 'Tarif',
    tablePlanActive: 'Tarif aktiv',
    tableUsage: 'Nutzung',
    tableResources: 'Ressourcen',
    tableCreated: 'Erstellt',
    tableActions: 'Aktionen',
    inactive: 'Inaktiv',
    admin: 'Admin',
    liveThisMonth: 'live diesen Monat',
    keys: 'Schlüssel',
    sessions: 'Sitzungen',
    updated: 'Aktualisiert',
    viewSelf: 'Mich anzeigen',
    viewDetails: 'Details anzeigen',
    usage: 'Nutzung',
    resources: 'Ressourcen',
    created: 'Erstellt',
    showingRange: '{start} bis {end} von {total} Nutzern',
    previous: 'Zurück',
    next: 'Weiter',
    exportStarted: 'CSV-Export gestartet.',
    unableExport: 'Nutzer konnten nicht exportiert werden.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super-Admin',
    demoRequests: 'Demo-Anfragen',
    subtitle: 'Demo-Buchungsanfragen von Interessenten verfolgen und verwalten',
    totalRequests: 'Anfragen gesamt',
    pending: 'Offen',
    contacted: 'Kontaktiert',
    completed: 'Abgeschlossen',
    allRequests: 'Alle Anfragen',
    pendingOnly: 'Nur offene',
    scheduled: 'Geplant',
    onboarded: 'Onboarded',
    followUp: 'Nachfassen',
    churned: 'Abgesprungen',
    showing: '{count} von {total} Anfragen',
    loading: 'Demo-Anfragen werden geladen...',
    noRequestsFound: 'Keine Demo-Anfragen gefunden',
    noRequestsAll:
      'Es wurden noch keine Demo-Anfragen eingereicht. Sie erscheinen hier, sobald Kunden auf Ihrer Landingpage Demos buchen.',
    noRequestsWithStatus: 'Keine Anfragen mit Status "{status}". Wählen Sie einen anderen Filter.',
    contact: 'Kontakt',
    quickInfo: 'Kurzinfo',
    status: 'Status',
    submitted: 'Eingegangen',
    actions: 'Aktionen',
    hideDetails: 'Details ausblenden',
    showDetails: 'Details anzeigen',
    noInfo: 'Keine Infos angegeben',
    email: 'E-Mail',
    role: 'Rolle',
    useCase: 'Anwendungsfall',
    expectedVolume: 'Erwartetes Volumen',
    message: 'Nachricht',
    viewFullDetails: 'Alle Details anzeigen',
    pageOf: 'Seite {page} von {total}',
    justNow: 'Gerade eben',
    minutesAgo: 'vor {count} Min.',
    hoursAgo: 'vor {count} Std.',
    daysAgo: 'vor {count} Tg.',
    statusUpdated: 'Status erfolgreich aktualisiert',
    statusUpdateFailed: 'Status konnte nicht aktualisiert werden',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Nutzerdetails werden geladen...',
    backToUsers: 'Zurück zu Nutzern',
    userNotFound: 'Nutzer nicht gefunden',
    superAdmin: 'Super-Admin',
    allUsers: 'Alle Nutzer',
    editUser: 'Nutzer bearbeiten',
    accountActions: 'Kontoaktionen',
    accountActionsBody:
      'Gesperrte Konten können sich nicht anmelden und keine neuen Verifizierungssitzungen erstellen.',
    suspending: 'Wird gesperrt...',
    suspendAccount: 'Konto sperren',
    reactivating: 'Wird reaktiviert...',
    reactivateAccount: 'Konto reaktivieren',
    updateUser: 'Nutzer aktualisieren',
    planTier: 'Tarifstufe',
    status: 'Status',
    free: 'Kostenlos',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Gesperrt',
    saveChanges: 'Änderungen speichern',
    saving: 'Wird gespeichert...',
    accountInformation: 'Kontoinformationen',
    email: 'E-Mail',
    name: 'Name',
    company: 'Firma',
    emailVerified: 'E-Mail verifiziert',
    verified: 'Verifiziert',
    notVerified: 'Nicht verifiziert',
    authProvider: 'Auth-Anbieter',
    created: 'Erstellt',
    lastUpdated: 'Zuletzt aktualisiert',
    planDetails: 'Tarifdetails',
    currentPlan: 'Aktueller Tarif',
    perMonth: 'Monat',
    testVerifications: 'Test-Verifizierungen',
    unlimited: 'Unbegrenzt',
    liveVerifications: 'Live-Verifizierungen',
    maxTestKeys: 'Max. Test-Schlüssel',
    maxLiveKeys: 'Max. Live-Schlüssel',
    suspendTitle: 'Dieses Konto sperren?',
    suspendDescription:
      'Gesperrte Nutzer können sich nicht anmelden und keine neuen Verifizierungssitzungen erstellen. Bestehende Daten bleiben erhalten.',
    suspend: 'Sperren',
    reactivateTitle: 'Dieses Konto reaktivieren?',
    reactivateDescription: 'Die Reaktivierung stellt den Zugriff sofort wieder her.',
    reactivate: 'Reaktivieren',
    totalSessions: 'Sitzungen gesamt',
    completed: 'Abgeschlossen',
    failed: 'Fehlgeschlagen',
    thisMonth: 'Dieser Monat',
    apiKeys: 'API-Schlüssel',
    totalKeys: 'Schlüssel gesamt',
    activeKeys: 'Aktive Schlüssel',
    publicId: 'Öffentliche ID',
    environment: 'Umgebung',
    lastUsed: 'Zuletzt verwendet',
    live: 'Live',
    test: 'Test',
    active: 'Aktiv',
    revoked: 'Widerrufen',
    never: 'Nie',
    showingRange: '{start} bis {end} von {total} Schlüsseln',
    previous: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {page} von {total}',
  },
};

// Keep translations concise in UI-critical flows; avoid idioms so they fit tight layouts.
const de: AdminUiCopy = {
  ...en,
  publicHeader: {
    features: 'Funktionen',
    howItWorks: 'So funktioniert es',
    pricing: 'Preise',
    blog: 'Blog',
    docs: 'Dokumentation',
    signIn: 'Anmelden',
    getApiKey: 'API-Schlüssel holen',
  },
  login: {
    ...en.login,
    authFailed: 'Authentifizierung fehlgeschlagen. Bitte erneut versuchen oder anderen Anbieter wählen.',
    sessionFailed: 'Anmeldung beim Anbieter erfolgreich, aber Sitzung konnte nicht erstellt werden.',
    notConfigured: 'SSO ist für diese Umgebung nicht konfiguriert.',
    invalidState: 'Sicherheitsprüfung fehlgeschlagen. Bitte erneut anmelden.',
    welcomeBack: 'Willkommen zurück',
    subtitle: 'Melden Sie sich bei Ihrem Dashboard an',
    accessRestricted: 'Zugriff eingeschränkt',
    accessDeniedAdminDisabled: 'Ihre E-Mail ist nicht berechtigt. Kontaktieren Sie support@walletgate.app, falls dies ein Fehler ist.',
    accessDeniedDefault: 'Ihre E-Mail ist nicht berechtigt.',
    contactSupport: 'Support kontaktieren',
    backToHome: 'Zur Startseite',
    continueWithEmail: 'oder mit E-Mail fortfahren',
    password: 'Passwort',
    forgotPassword: 'Passwort vergessen?',
    signIn: 'Anmelden',
    noAccount: 'Noch kein Konto?',
    createAccount: 'Konto erstellen',
  },
  register: {
    ...en.register,
    createAccount: 'Konto erstellen',
    subtitle: 'Starten Sie mit einem kostenlosen Tarif - keine Kreditkarte erforderlich',
    accessRestrictedBody: 'Registrierung ist derzeit eingeschränkt. Kontaktieren Sie support@walletgate.app für Zugriff.',
    welcome: 'Willkommen bei WalletGate!',
    accountCreated: 'Ihr Konto wurde erstellt',
    testApiKey: 'Ihr Test-API-Schlüssel (jetzt speichern):',
    keyShownOnce: 'Dieser Schlüssel wird nur einmal angezeigt. Weitere Schlüssel können im Dashboard erstellt werden.',
    redirecting: 'Weiterleitung zum Dashboard in 5 Sekunden...',
    goNow: 'Jetzt zum Dashboard',
    fullName: 'Vollständiger Name',
    companyOptional: 'Unternehmen (optional)',
    password: 'Passwort',
    passwordStrength: 'Passwortstärke',
    tooShort: 'Zu kurz (mind. 8)',
    moderate: 'Mittel',
    strong: 'Stark',
    creating: 'Konto wird erstellt…',
    haveAccount: 'Sie haben bereits ein Konto?',
    signIn: 'Anmelden',
    registrationFailed: 'Registrierung fehlgeschlagen',
    teamInviteAcceptedTitle: 'Teameinladung angenommen',
    teamInviteAcceptedBody: 'Sie wurden dem Team hinzugefügt. Weiterleitung zum Team-Dashboard...',
    inviteLockedEmailHint: 'Diese E-Mail ist mit Ihrer Teameinladung verknüpft und kann nicht geändert werden.',
  },
  forgotPassword: {
    ...en.forgotPassword,
    checkEmail: 'E-Mail prüfen',
    accountExistsNoticePrefix: 'Wenn ein Konto für',
    accountExistsNoticeSuffix: 'vorhanden ist, erhalten Sie in Kürze einen Link zum Zurücksetzen des Passworts.',
    nextSteps: 'Nächste Schritte:',
    stepInbox: 'Posteingang auf die Reset-E-Mail prüfen',
    stepLink: 'Link in der E-Mail anklicken (1 Stunde gültig)',
    stepNewPassword: 'Neues Passwort erstellen',
    backToSignIn: 'Zurück zur Anmeldung',
    title: 'Passwort vergessen?',
    subtitle: 'Geben Sie Ihre E-Mail ein, wir senden einen Reset-Link',
    sending: 'Wird gesendet…',
    sendResetLink: 'Reset-Link senden',
    rememberPassword: 'Passwort wieder eingefallen?',
    signIn: 'Anmelden',
    sendFailed: 'Reset-E-Mail konnte nicht gesendet werden',
  },
  resetPassword: {
    ...en.resetPassword,
    invalidLink: 'Ungültiger Reset-Link. Bitte fordern Sie einen neuen Link an.',
    tooShort: 'Passwort muss mindestens 8 Zeichen haben',
    mismatch: 'Passwörter stimmen nicht überein',
    failed: 'Passwort konnte nicht zurückgesetzt werden',
    successTitle: 'Passwort zurückgesetzt',
    successBody: 'Ihr Passwort wurde erfolgreich zurückgesetzt.',
    successCta: 'Jetzt anmelden',
    redirecting: 'Weiterleitung zur Anmeldung in 3 Sekunden...',
    title: 'Passwort zurücksetzen',
    subtitle: 'Geben Sie Ihr neues Passwort ein',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    passwordStrength: 'Passwortstärke',
    resetting: 'Wird zurückgesetzt…',
    reset: 'Passwort zurücksetzen',
    rememberPassword: 'Passwort wieder eingefallen?',
    signIn: 'Anmelden',
  },
  notFound: {
    pageNotFound: 'Seite nicht gefunden',
    description: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    goBack: 'Zurück',
    goDashboard: 'Zum Dashboard',
    supportPrefix: 'Wenn Sie glauben, dass dies ein Fehler ist, kontaktieren Sie bitte',
  },
  teamManagement: {
    title: 'Team',
    subtitleWithCount: '{current} von {max} Mitgliedern',
    subtitleDefault: 'Teammitglieder verwalten',
    leaveTeam: 'Team verlassen',
    planLockedTitle: 'Teamverwaltung ist in kostenpflichtigen Tarifen verfügbar',
    planLockedBody: 'Upgrade auf Pro für bis zu 3 Teammitglieder oder Business für bis zu 10.',
    viewPlans: 'Tarife ansehen',
    inviteMemberTitle: 'Teammitglied einladen',
    inviteCopiedTitle: 'Einladungslink in die Zwischenablage kopiert',
    copyAgain: 'Erneut kopieren',
    inviteLinkHint: 'Teilen Sie diesen Link mit Ihrem Teammitglied. Er läuft in 7 Tagen ab.',
    invitePlaceholder: 'kollege@firma.de',
    roleAdmin: 'Admin',
    roleDeveloper: 'Entwickler',
    roleViewer: 'Betrachter',
    roleAdminDescription: 'Voller Zugriff',
    roleDeveloperDescription: 'Schlüssel und Playground verwalten',
    roleViewerDescription: 'Nur Lesezugriff',
    sendInvite: 'Einladung senden',
    sendingInvite: 'Wird gesendet...',
    limitReachedPrefix: 'Teamlimit erreicht.',
    upgradePlan: 'Tarif upgraden',
    membersTitle: 'Mitglieder',
    owner: 'Inhaber',
    ownerDescription: 'Kontoinhaber',
    joinedOn: 'Beigetreten am {date}',
    pending: 'Ausstehend',
    remove: 'Entfernen',
    noMembers: 'Noch keine Teammitglieder.',
    noMembersCta: 'Senden Sie eine Einladung, um zu starten.',
    pendingInvitesTitle: 'Ausstehende Einladungen',
    expires: 'Läuft ab am {date}',
    copyLink: 'Link kopieren',
    revoke: 'Widerrufen',
    leaveDialogTitle: 'Team verlassen',
    leaveDialogBody:
      'Möchten Sie dieses Team wirklich verlassen? Sie verlieren den Zugriff auf die Ressourcen dieses Kontos und benötigen eine neue Einladung für den Wiedereintritt.',
    leaveDialogConfirm: 'Team verlassen',
    removeDialogTitle: 'Teammitglied entfernen',
    removeDialogBody: '{email} aus dem Team entfernen? Der Zugriff wird sofort entzogen.',
    removeDialogConfirm: 'Entfernen',
    revokeDialogTitle: 'Einladung widerrufen?',
    revokeDialogBody: 'Die Einladung an {email} wird storniert. Die Person kann dem Team nicht beitreten.',
    toastInviteCopied: 'Einladungslink in die Zwischenablage kopiert',
    toastCopiedAgain: 'Erneut kopiert',
    toastInviteRevoked: 'Einladung widerrufen',
    toastInviteRevokeFailed: 'Einladung konnte nicht widerrufen werden',
    toastMemberRemoved: 'Teammitglied entfernt',
    toastMemberRemoveFailed: 'Teammitglied konnte nicht entfernt werden',
    toastRoleUpdated: 'Rolle aktualisiert',
    toastRoleUpdateFailed: 'Rolle konnte nicht aktualisiert werden',
    toastLeftTeam: 'Sie haben das Team verlassen',
    toastLeaveTeamFailed: 'Team konnte nicht verlassen werden',
    inviteSendFailed: 'Einladung konnte nicht gesendet werden',
  },
  auditLog: {
    title: 'Audit-Log',
    subtitle: 'Alle Aktivitäten in Ihrem Konto nachverfolgen.',
    searchPlaceholder: 'Nach Aktion, Akteur, Ziel, IP, Request-ID oder Ereignis-ID suchen',
    allCategories: 'Alle Kategorien',
    categorySecurity: 'Sicherheit',
    categoryApi: 'API',
    categoryDataAccess: 'Datenzugriff',
    categoryConfiguration: 'Konfiguration',
    allSeverities: 'Alle Schweregrade',
    severityInfo: 'Info',
    severityWarning: 'Warnung',
    severityError: 'Fehler',
    severityCritical: 'Kritisch',
    clearFilters: 'Filter zurücksetzen',
    loading: 'Audit-Logs werden geladen...',
    empty: 'Keine Audit-Einträge gefunden.',
    noMatches: 'Keine passenden Einträge auf dieser Seite.',
    timestamp: 'Zeitstempel',
    eventKey: 'Ereignisschlüssel',
    action: 'Aktion',
    actor: 'Akteur',
    target: 'Ziel',
    category: 'Kategorie',
    severity: 'Schweregrad',
    source: 'Quelle',
    ipAddress: 'IP-Adresse',
    eventId: 'Ereignis-ID',
    requestId: 'Request-ID',
    userAgent: 'User-Agent',
    unknown: 'Unbekannt',
    system: 'System',
    pageSummary: 'Seite {page} von {totalPages} ({total} Einträge)',
    previous: 'Zurück',
    next: 'Weiter',
    tapToExpand: 'Tippen für Details',
    details: 'Details',
  },
  inviteAccept: {
    loading: 'Einladungsdetails werden geladen...',
    noToken: 'Kein Einladungstoken angegeben.',
    invalidInviteDefault: 'Diese Einladung ist ungültig, abgelaufen oder bereits verwendet.',
    acceptFailedDefault: 'Einladung konnte nicht angenommen werden. Sie ist möglicherweise abgelaufen oder widerrufen.',
    inviteTitle: 'Sie wurden eingeladen',
    inviteSubtitleNeedsLogin: 'Melden Sie sich an oder erstellen Sie ein Konto, um beizutreten',
    organization: 'Organisation',
    invitedBy: 'Eingeladen von',
    yourRole: 'Ihre Rolle',
    role: 'Rolle',
    acceptInvite: 'Einladung annehmen',
    joiningTeam: 'Teambeitritt läuft...',
    successTitle: 'Sie sind dabei!',
    successBodyWithOrg: 'Sie sind {organization} beigetreten. Weiterleitung...',
    successBodyDefault: 'Sie sind dem Team beigetreten. Weiterleitung...',
    errorTitle: 'Einladung konnte nicht angenommen werden',
    goLogin: 'Zur Anmeldung',
    signInToAccept: 'Anmelden und annehmen',
    createAccount: 'Konto erstellen',
    signIn: 'Anmelden',
    needsRegisterTitle: 'Konto erstellen',
    needsRegisterBodyWithOrg: 'Erstellen Sie ein Konto mit {email}, um {organization} beizutreten.',
    needsRegisterBodyDefault: 'Erstellen Sie ein Konto mit {email}, um dem Team beizutreten.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Entwickler',
    roleViewer: 'Betrachter',
    roleAdminDescription: 'Voller Zugriff auf Schlüssel, Webhooks, Abrechnung und Team',
    roleDeveloperDescription: 'API-Schlüssel erstellen und verwalten, Playground nutzen',
    roleViewerDescription: 'Nur Lesezugriff auf Abrechnung und Audit-Logs',
  },
  keys: {
    ...en.keys,
    title: 'API-Schlüssel',
    createEnvLabel: 'Umgebung für Schlüssel erstellen',
    testKey: 'Test-Schlüssel',
    liveKey: 'Live-Schlüssel',
    createKey: '+ Schlüssel erstellen',
    maxKeysReached: 'Maximale Anzahl {env}-Schlüssel für Ihren Tarif erreicht',
    defaultHint: 'Standardmäßig Test für sichere Entwicklung verwenden.',
    limitReached: '{env}-Schlüssel-Limit erreicht.',
    revokeExisting: 'Führen Sie ein Upgrade durch oder widerrufen Sie einen bestehenden Schlüssel.',
    testKeys: 'Test-Schlüssel',
    liveKeys: 'Live-Schlüssel',
    upgrade: 'Upgrade',
    newKeyShowOnce: 'Neuer API-Schlüssel (einmalig sichtbar)',
    rotatedKeyShowOnce: 'Rotierter API-Schlüssel (einmalig sichtbar)',
    freePlan: 'Kostenloser Tarif',
    freePlanHint: 'Upgrade auf Pro (29 €/Monat) für 1.000 Live-Verifizierungen, Webhooks und mehr API-Schlüssel.',
    filters: 'Filter',
    hideFilters: 'Filter ausblenden',
    showFilters: 'Filter anzeigen',
    allStatus: 'Alle Status',
    activeOnly: 'Nur aktive',
    revokedOnly: 'Nur widerrufene',
    allEnvironments: 'Alle Umgebungen',
    keyNamePlaceholder: 'z. B. Production API',
    loading: 'API-Schlüssel werden geladen...',
    emptyAll: 'Noch keine API-Schlüssel.',
    emptyByStatus: 'Keine {status} API-Schlüssel gefunden.',
    unnamed: 'Unbenannt',
    revoked: 'Widerrufen',
    active: 'Aktiv',
    unknown: 'Unbekannt',
    rotate: 'Rotieren',
    revoke: 'Widerrufen',
    rotateConfirmTitle: 'API-Schlüssel rotieren?',
    rotateConfirmDescription: 'Es wird ein neuer Schlüssel für "{name}" erstellt und der aktuelle sofort widerrufen.',
    revokeConfirmTitle: 'API-Schlüssel widerrufen?',
    revokeConfirmDescription: '"{name}" wird dauerhaft deaktiviert. Diese Aktion kann nicht rückgängig gemacht werden.',
    toastPendingKey: 'Ihr neuer Test-API-Schlüssel ist bereit. Jetzt kopieren; später nicht mehr sichtbar.',
    toastCreated: 'API-Schlüssel erstellt. Jetzt kopieren; später nicht mehr sichtbar.',
    toastCreateFailed: 'Schlüssel konnte nicht erstellt werden',
    toastRotated: 'API-Schlüssel rotiert. Jetzt kopieren.',
    toastRotateFailed: 'Schlüssel konnte nicht rotiert werden',
    toastRevoked: 'API-Schlüssel widerrufen',
    toastRevokeFailed: 'Schlüssel konnte nicht widerrufen werden',
    toastUpgradeRequired: 'Upgrade erforderlich - Tarife in der Abrechnung ansehen',
  },
  webhooks: {
    ...en.webhooks,
    upgradeRequired: 'Upgrade erforderlich',
    upgradeBody: 'Webhooks sind im Business-Tarif verfügbar.',
    title: 'Webhooks',
    subtitle: 'Erhalten Sie Echtzeit-Events für Ihre Integrationen',
    createTitle: 'Webhook erstellen',
    webhookUrl: 'Webhook-URL (HTTPS)',
    events: 'Events',
    createWebhook: 'Webhook erstellen',
    signingSecret: 'Webhook-Signatur-Secret (jetzt speichern)',
    existingTitle: 'Bestehende Webhooks',
    loading: 'Wird geladen...',
    empty: 'Noch keine Webhooks.',
    enabled: 'Aktiv',
    disabled: 'Deaktiviert',
    copyUrl: 'URL kopieren',
    deleteWebhook: 'Löschen',
    deleteConfirm: 'Möchten Sie diesen Webhook wirklich löschen? Dies kann nicht rückgängig gemacht werden.',
    deleted: 'Webhook gelöscht.',
    deleteFailed: 'Webhook konnte nicht gelöscht werden.',
    inspectorTitle: 'Webhook-Inspektor',
    inspectorLocked: 'Webhook-Inspektor ist in Pro- und Business-Tarifen verfügbar.',
    loadingDeliveries: 'Lieferungen werden geladen...',
    emptyDeliveries: 'Noch keine Webhook-Lieferungen.',
    webhookFallbackName: 'Webhook',
    deliveryId: 'Liefer-ID',
    statusLabel: 'Status',
    attemptLabel: 'Versuch',
    errorLabel: 'Fehler',
    payload: 'Payload',
    signature: 'Signatur',
    responseBody: 'Antwortinhalt',
    retrying: 'Wiederholung…',
    retryDelivery: 'Lieferung erneut senden',
    selectDelivery: 'Wählen Sie eine Lieferung zur Ansicht aus.',
    clipboardUnavailable: 'Zwischenablage ist in diesem Browser nicht verfügbar.',
    copied: 'Webhook-URL kopiert.',
    copyFailed: 'Webhook-URL konnte nicht kopiert werden.',
    urlMustHttps: 'URL muss HTTPS verwenden',
    urlUseServer: 'Verwenden Sie Ihre Server-URL (nicht die WalletGate-Konsole)',
    urlInvalid: 'Geben Sie eine gültige URL ein (https://...)',
    created: 'Webhook erstellt. Speichern Sie Ihr Signatur-Secret.',
    createFailed: 'Webhook konnte nicht erstellt werden. Prüfen Sie HTTPS und Erreichbarkeit der URL.',
    replayed: 'Lieferung erneut gesendet.',
    replayFailed: 'Webhook-Lieferung konnte nicht erneut gesendet werden.',
  },
  billing: {
    ...en.billing,
    breadcrumb: 'Abrechnung',
    staffBypass: 'Staff-Konto: Berechtigungen umgangen. Abrechnung und Quoten gelten nicht.',
    paidComingSoonTitle: 'Bezahlte Tarife kommen bald.',
    paidComingSoonBody: 'Wir finalisieren derzeit unsere Zahlungsintegration. Bis dahin können Sie den Free-Tarif nutzen.',
    upgradeDetected: 'Upgrade auf {plan} erkannt. Premium-Funktionen wie KI-Anomalieerkennung und Webhooks sind jetzt verfügbar.',
    upgradePending: 'Wenn Sie den Checkout für {plan} abgeschlossen haben, erkennen wir das Upgrade in Kürze automatisch.',
    refreshNow: 'Jetzt aktualisieren',
    goSecurity: 'Zur Sicherheit',
    title: 'Tarife & Abrechnung',
    subtitle: 'Wählen Sie den passenden Tarif. Upgrade jederzeit möglich.',
    manageBilling: 'Abrechnung verwalten',
    currentPlan: 'Aktueller Tarif',
    tierLabel: 'Tarif: {tier}',
    noBillingStaff: 'Keine Abrechnung für Staff',
    perMonth: 'pro Monat',
    billingCycle: 'Abrechnungszyklus',
    nextBillDate: 'Nächstes Abrechnungsdatum',
    noBillingCycle: 'Noch kein Abrechnungszyklus. Führen Sie ein Upgrade für ein Abonnement durch.',
    noBillingCycleStaff: 'Internes Konto. Kein Abrechnungszyklus.',
    manageBillingArrow: 'Abrechnung verwalten →',
    freeFeatures: [
      'Unbegrenzte Test-Verifizierungen',
      '100 Live-Verifizierungen/Monat',
      '5 Test-Schlüssel, 2 Live-Schlüssel',
      'Basis-Sandbox (Bestanden/Nicht bestanden)',
      'Integrations-Codebeispiele (10 Sprachen)',
      'E-Mail-Support',
    ],
    proFeatures: [
      'Unbegrenzte Test-Verifizierungen',
      '1.000 Live-Verifizierungen/Monat',
      '10 Test-Schlüssel, 5 Live-Schlüssel',
      'Bis zu 3 Teammitglieder',
      '20+ vorkonfigurierte Testszenarien',
      'Webhook-Inspektor',
      '99,9 % SLA',
      'E-Mail-Support',
    ],
    businessFeatures: [
      'Unbegrenzte Test-Verifizierungen',
      '10.000 Live-Verifizierungen/Monat',
      '10 Test-Schlüssel, 10 Live-Schlüssel',
      'Bis zu 10 Teammitglieder',
      'Alles aus Pro',
      'Eigene Szenarien erstellen',
      'EUDI-Readiness-Bericht (Compliance-Export)',
      'Ländersimulation (DE, FR, ES, IT, NL)',
      'Erweiterte Analysen',
      'Priorisierter E-Mail-Support',
    ],
    mostPopular: 'Beliebtester Tarif',
    activePlan: 'Aktiver Tarif',
    currentPlanCta: 'Aktueller Tarif',
    downgrade: 'Downgrade',
    upgradeToPro: 'Auf Pro upgraden',
    upgradeToBusiness: 'Auf Business upgraden',
    managePlan: 'Tarif verwalten',
    currentOrLowerPlan: 'Aktueller oder niedrigerer Tarif',
    comingSoon: 'Bald verfügbar',
    enterpriseTitle: 'Mehr als 10.000 Verifizierungen benötigt?',
    enterpriseBody: 'Kontaktieren Sie uns für individuelle Preise mit Mengenrabatten, dediziertem Support und SLAs.',
    contactSales: 'Vertrieb kontaktieren',
    downgradeHint: 'Zum Downgrade nutzen Sie das Abrechnungsportal. Änderungen gelten ab Ende Ihres Abrechnungszeitraums.',
    toastUpgradeSuccess: 'Upgrade auf {plan} erfolgreich. Zeitanteilige Kosten erscheinen auf der nächsten Rechnung.',
    toastOpenPortalFailed: 'Abrechnungsportal konnte nicht geöffnet werden',
    toastCheckoutFallback: 'Checkout nicht verfügbar. Abrechnungsportal wird geöffnet...',
  },
  dashboard: {
    ...en.dashboard,
    overview: 'Übersicht',
    noPriorData: 'Keine vorherigen Daten',
    noChange: 'Keine Veränderung',
    vsLastPeriod: '{value}% gegenüber Vorperiode',
    last24Hours: 'Letzte 24 Stunden',
    last30Days: 'Letzte 30 Tage',
    last7Days: 'Letzte 7 Tage',
    hour: 'Stunde',
    day: 'Tag',
    totalVerifications: 'Verifizierungen gesamt',
    successRate: 'Erfolgsquote',
    activeSessions: 'Aktive Sitzungen',
    currentlyInProgress: 'Derzeit in Bearbeitung',
    verificationTrend: 'Verifizierungstrend',
    trackVerificationVolume: 'Verifizierungsvolumen im Zeitverlauf verfolgen',
    total: 'Gesamt',
    completed: 'Abgeschlossen',
    failed: 'Fehlgeschlagen',
    average: 'Durchschnitt',
    peak: 'Spitze',
    perBucket: 'Pro {bucket}',
    avgPerBucket: 'Durchschn. pro {bucket}',
    noDataYet: 'Noch keine Daten',
    verificationsWillAppear: 'Verifizierungen erscheinen hier, sobald Sie starten',
    currentPlan: 'Aktueller Tarif',
    manage: 'Verwalten',
    staff: 'Staff',
    internalAccount: 'Internes Konto',
    noBillingForStaff: 'Keine Abrechnung für Staff',
    liveVerificationsThisMonth: 'Live-Verifizierungen (diesen Monat)',
    upgradeForLiveAccess: 'Upgrade für Live-Zugriff',
    nextBillDate: 'Nächstes Abrechnungsdatum',
    status: 'Status',
    daysRemaining: '{days} Tage verbleibend',
    renewingSoon: 'Bald erneuernd',
    gracePeriod: 'Kulanzfrist',
    daysToUpdatePayment: '{days} Tage zur Zahlungsaktualisierung',
    active: 'Aktiv',
    allSystemsOperational: 'Alle Systeme betriebsbereit',
    advancedAnalytics: 'Erweiterte Analysen',
    detailedBreakdown: 'Detailaufschlüsselung nach Umgebung und API-Schlüssel',
    exportCsv: 'CSV exportieren',
    byEnvironment: 'Nach Umgebung',
    byStatus: 'Nach Status',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top API-Schlüssel (diesen Monat)',
    advancedAnalyticsUpgrade: 'Erweiterte Analysen. Upgrade für Detailaufschlüsselungen und CSV-Export.',
    upgrade: 'Upgrade',
    developerResources: 'Entwicklerressourcen',
    documentation: 'Dokumentation',
    typescriptSdk: 'TypeScript-SDK',
    onboardingWelcome: 'Willkommen bei WalletGate',
    youreAllSet: 'Alles bereit!',
    accountReady: 'Ihr Konto ist bereit. Führen Sie Ihre erste Verifizierung in unter 5 Minuten durch.',
    getStartedIn3Steps: 'In 3 Schritten starten',
    getYourApiKey: 'API-Schlüssel holen',
    apiKeyReady: 'Sie haben bereits einen einsatzbereiten API-Schlüssel',
    createFirstApiKey: 'Erstellen Sie Ihren ersten API-Schlüssel zur Authentifizierung von Anfragen',
    viewApiKeys: 'API-Schlüssel anzeigen',
    createApiKey: 'API-Schlüssel erstellen',
    installSdk: 'SDK installieren',
    addWalletGate: 'WalletGate mit einem einzigen Befehl zum Projekt hinzufügen',
    copyCommand: 'Befehl kopieren',
    copied: 'Kopiert!',
    makeFirstRequest: 'Erste Anfrage senden',
    startVerifying: 'Beginnen Sie mit der Verifizierung digitaler Identitäten mit wenigen Codezeilen',
    viewDocumentation: 'Dokumentation anzeigen',
    quickStartExample: 'Schnellstart-Beispiel',
    copyCode: 'Code kopieren',
    apiDocumentation: 'API-Dokumentation',
    apiDocumentationDesc: 'Vollständige API-Referenz und Anleitungen',
    codeExamples: 'Codebeispiele',
    codeExamplesDesc: 'Beispielintegrationen in mehreren Sprachen',
    support: 'Support',
    supportDesc: 'Hilfe von unserem Team erhalten',
    community: 'Community',
    communityDesc: 'Andere Entwickler treffen',
    complete: 'Abgeschlossen',
    noPriorRange: 'Keine Daten für vorherige {range}',
    noChangeVsPrior: 'Keine Veränderung gegenüber vorherigem {range}',
    vsPriorRange: '{value}% gegenüber vorherigem {range}',
  },
  playground: {
    ...en.playground,
    export: 'Exportieren',
    quickTour: 'Kurztour',
    stepOf: 'Schritt {current} von {total}',
    skip: 'Überspringen',
    nextTip: 'Nächster Tipp',
    done: 'Fertig',
    quickStart: 'Schnellstart',
    quickStartSubtitle: 'Wählen Sie einen Anwendungsfall und verifizieren Sie in Sekunden oder konfigurieren Sie selbst.',
    onboardingConfigureTitle: 'Prüfungen konfigurieren',
    onboardingConfigureMessage: 'Wählen Sie eine Vorlage oder fügen Sie Prüfungen im Konfigurationsbereich hinzu und starten Sie dann die Verifizierung.',
    onboardingFlowTitle: 'Live-Ablauf ansehen',
    onboardingFlowMessage: 'Verfolgen Sie jeden Meilenstein vom Wallet-Handoff bis zur Entscheidung.',
    onboardingShipTitle: 'Schneller ausliefern',
    onboardingShipMessage: 'Exportieren Sie den Lauf als JSON oder kopieren Sie den Integrationscode für Ihre App.',
    configureNow: 'Jetzt konfigurieren',
    tryNow: 'Jetzt testen →',
    checksConfigured: '{count} Prüfungen konfiguriert',
    howConfidenceMeasured: 'So wird Vertrauen berechnet',
    sandboxToolkit: 'Sandbox-Toolkit',
    scenarioName: 'Szenarioname',
    scenarioNamePlaceholder: 'z. B. Minderjährig + EU-Wohnsitz',
    description: 'Beschreibung',
    scenarioDescriptionPlaceholder: 'Optionale Zusammenfassung für Ihr Team',
    defaultResult: 'Standardergebnis',
    defaultPass: 'Standard: bestanden',
    defaultFail: 'Standard: nicht bestanden',
    checkOverrides: 'Prüfungsüberschreibungen',
    allPass: 'Alle bestehen',
    allFail: 'Alle scheitern',
    addChecksHint: 'Fügen Sie oben Prüfungen hinzu, um Szenarioergebnisse zu konfigurieren.',
    saveScenario: 'Szenario speichern',
    saving: 'Wird gespeichert...',
    createScenarioFailed: 'Szenario konnte nicht erstellt werden',
    savedScenarios: 'Gespeicherte Szenarien',
    noCustomScenarios: 'Noch keine benutzerdefinierten Szenarien. Erstellen Sie eins zur Wiederverwendung.',
    use: 'Verwenden',
    useScenarioTitle: 'Prüfungen laden und Szenario für Simulation vorauswählen',
    deleteScenarioConfirm: 'Dieses Szenario löschen?',
    defaultPassLabel: 'Standard bestanden',
    defaultFailLabel: 'Standard nicht bestanden',
    range: 'Zeitraum',
    runsCaptured: '{count} Runs in diesem Zeitraum erfasst.',
    checksPass: '{passed}/{total} Prüfungen bestanden',
    runsUsed: '{used} / {total} Ausführungen verbraucht',
  },
  notifications: {
    title: 'Benachrichtigungen',
    subtitle: 'Bleiben Sie informiert über Kontoaktivitäten, Sicherheitswarnungen und Teamupdates.',
    searchPlaceholder: 'Benachrichtigungen durchsuchen...',
    allCategories: 'Alle Kategorien',
    categorySecurity: 'Sicherheit',
    categoryBilling: 'Abrechnung',
    categoryApi: 'API',
    categoryTeam: 'Team',
    allStatus: 'Alle',
    unreadOnly: 'Ungelesen',
    readOnly: 'Gelesen',
    clearFilters: 'Filter zurücksetzen',
    loading: 'Benachrichtigungen werden geladen...',
    empty: 'Noch keine Benachrichtigungen. Aktivitäten erscheinen hier, sobald sie auftreten.',
    noMatches: 'Keine Benachrichtigungen entsprechen Ihren Filtern.',
    markRead: 'Als gelesen markieren',
    markAllRead: 'Alle als gelesen markieren',
    delete: 'Löschen',
    viewAll: 'Alle Benachrichtigungen anzeigen',
    preferences: 'Einstellungen',
    preferencesTitle: 'Benachrichtigungseinstellungen',
    preferencesSubtitle: 'Wählen Sie, wie Sie für jede Kategorie benachrichtigt werden möchten.',
    inApp: 'In-App',
    email: 'E-Mail',
    savePreferences: 'Einstellungen speichern',
    savedPreferences: 'Benachrichtigungseinstellungen gespeichert.',
    failedSavePreferences: 'Benachrichtigungseinstellungen konnten nicht gespeichert werden.',
    category: 'Kategorie',
    severity: 'Schweregrad',
    timestamp: 'Zeit',
    pageSummary: 'Seite {page} von {total}',
    previous: 'Zurück',
    next: 'Weiter',
    bellNoNotifications: 'Keine neuen Benachrichtigungen',
    bellRecentTitle: 'Benachrichtigungen',
    severityInfo: 'Info',
    severityWarning: 'Warnung',
    severityError: 'Fehler',
    severityCritical: 'Kritisch',
    toastMarkedRead: 'Benachrichtigung als gelesen markiert.',
    toastMarkedAllRead: 'Alle Benachrichtigungen als gelesen markiert.',
    toastDeleted: 'Benachrichtigung gelöscht.',
    toastDeleteFailed: 'Benachrichtigung konnte nicht gelöscht werden.',
    toastNewSingle: 'Sie haben 1 neue Benachrichtigung.',
    toastNewMultiple: 'Sie haben {count} neue Benachrichtigungen.',
  },
  ...deExtendedSections,
};

export default de;
